import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { getApiErrorMessage } from "../../services/api";
import PaymentStep from "../../components/QuestionTypes/PaymentStep";
import TreatmentOrderSubmitted from "./TreatmentOrderSubmitted";
import { buildLiveOrderSummary } from "./previewSimulation";
import {
  isCombinedCheckoutSuccessful,
  productCategoryIdentity,
  productSelectionError,
} from "./checkoutContract";
import { normalizePaymentMethod, paymentMethodToken } from "./authorityPayload";
import {
  requoteTreatmentCheckout,
  selectionsFromCheckout,
} from "./treatmentCheckoutRequote";
import TreatmentProductSelection, { resolveProductImageUrl } from "./TreatmentProductSelection";
import { TREATMENT_CHECKOUT_ERROR_COPY } from "./treatmentUiConstants";
import { patientProductName } from "./patientProductName";
import { pickQuestionnaireLogoUrl } from "../../utils/questionnaireBranding";
import {
  clearTreatmentCheckout,
  legacyTreatmentCheckoutStorageKey,
  persistRepricedCheckout,
  treatmentCheckoutStorageKey,
} from "./treatmentCheckoutStorage";
import {
  CHECKOUT_REFRESHED_NOTICE,
  CHECKOUT_UPDATED_NOTICE,
  adoptRecoveredCheckout,
  isStaleCheckoutError,
  isUsableCheckout,
} from "./treatmentCheckoutRefresh";
import "./treatmentCheckoutPage.css";

export { treatmentCheckoutStorageKey } from "./treatmentCheckoutStorage";
const attachRuntimeApiBaseUrl = (checkout, apiBaseUrl) => (
  checkout
  && !checkout.runtime_api_base_url
  && apiBaseUrl
    ? { ...checkout, runtime_api_base_url: apiBaseUrl }
    : checkout
);

const money = (value, currency = "USD") => {
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(Number(value || 0));
  } catch (_error) {
    return `$${Number(value || 0).toFixed(2)}`;
  }
};

const patientProductDescription = (product) => {
  const regimen = String(
    product?.regimen
    || product?.titration_category
    || product?.regimen_name
    || product?.regimenName
    || "Standard regimen",
  ).trim() || "Standard regimen";
  const supply = product?.days_supply
    ? `${product.days_supply}-day supply${Number(product.refills || 0) > 0 ? ` + ${product.refills} refill${Number(product.refills) === 1 ? "" : "s"}` : ""}`
    : "Medication";
  const quantity = Number(product?.quantity || 1) > 1
    ? ` · Qty ${product.quantity}`
    : "";
  return `${regimen} · ${supply}${quantity}`;
};

const patientLabDescription = (lab) => {
  const details = [];
  const markerCount = Number(lab?.marker_count || lab?.markers?.length || 0);
  const provider = String(
    lab?.lab_provider
    || lab?.lab_name
    || lab?.provider_network
    || lab?.provider
    || "",
  ).trim();
  if (provider) details.push(provider);
  if (markerCount > 0) details.push(`${markerCount} markers`);
  details.push("one-time");
  return details.join(" · ");
};

const patientLabPrice = (lab) => (
  lab?.line_total
  ?? lab?.patient_price
  ?? lab?.financials?.patient?.line_total
  ?? "0.00"
);

export default function TreatmentCheckoutPage({ token, clientConfig }) {
  const [checkout, setCheckout] = useState(() => {
    try {
      const serialized = sessionStorage.getItem(treatmentCheckoutStorageKey(token))
        || sessionStorage.getItem(legacyTreatmentCheckoutStorageKey(token));
      return attachRuntimeApiBaseUrl(
        JSON.parse(serialized || "null"),
        clientConfig?.api_endpoint,
      );
    } catch (_error) {
      return null;
    }
  });
  const checkoutRef = useRef(checkout);
  checkoutRef.current = checkout;
  const [checkoutInitializing, setCheckoutInitializing] = useState(() => !checkout);
  const [checkoutNotice, setCheckoutNotice] = useState("");
  const [couponInput, setCouponInput] = useState("");
  const [couponError, setCouponError] = useState("");
  const [couponBusy, setCouponBusy] = useState(false);
  const [editingProducts, setEditingProducts] = useState(false);
  const [productDraft, setProductDraft] = useState([]);
  const [productEditBusy, setProductEditBusy] = useState(false);
  const [productEditError, setProductEditError] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [checkoutRecovery, setCheckoutRecovery] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkoutPaymentConfig, setCheckoutPaymentConfig] = useState(null);
  const [checkoutSavedPaymentMethods, setCheckoutSavedPaymentMethods] = useState([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
  const paymentRef = useRef(null);
  const activeCheckoutToken = useRef(token);
  const selectionEdit = checkoutRecovery?.selection_edit || checkout?.selection_edit;
  const selectionEditBlocked = selectionEdit?.allowed === false;
  const summary = useMemo(() => buildLiveOrderSummary(checkout), [checkout]);
  const productEditSelectionError = useMemo(() => {
    const choices = (checkout?.treatments || []).flatMap((treatment) => (
      treatment.product_options || []
    ));
    if (!choices.length) return "";
    return productSelectionError(
      {
        question_type: "product_selection",
        selection_mode: "multiple",
        min_selections: 1,
        answer_choices: choices,
      },
      productDraft,
    );
  }, [checkout, productDraft]);
  const activeCoupon = checkout?.coupon || checkout?.pricing?.coupon;
  const appliedCoupon = activeCoupon?.code && !activeCoupon?.error ? activeCoupon : null;

  const beginProductEdit = () => {
    if (selectionEditBlocked) return;
    setProductEditError("");
    const optionsById = new Map(
      (checkout?.treatments || []).flatMap((item) => item.product_options || [])
        .map((option) => [String(option.option_id || ""), option]),
    );
    // Carry the full option identity so the editor matches Required cards instead of re-adding them.
    setProductDraft(selectionsFromCheckout(checkout).map((selection) => ({
      ...optionsById.get(String(selection.option_id)),
      ...selection,
    })));
    setEditingProducts(true);
  };

  const cancelProductEdit = () => {
    if (productEditBusy) return;
    setProductEditError("");
    setEditingProducts(false);
    setProductDraft([]);
  };

  const updateTreatmentProductDraft = (treatment, nextSelection) => {
    setProductEditError("");
    const optionIds = new Set(
      (treatment.product_options || []).map((option) => String(option.option_id || "")),
    );
    const optionsById = new Map(
      (summary?.treatments || []).flatMap((item) => item.product_options || [])
        .map((option) => [String(option.option_id || ""), option]),
    );
    const incomingSelections = (nextSelection || []).map((selection) => ({
      ...optionsById.get(String(selection.option_id || selection.value || "")),
      option_id: String(selection.option_id || selection.value || ""),
      quantity: Number(selection.quantity || 1),
      ...(Number(selection.days_supply) > 0
        ? { days_supply: Number(selection.days_supply) }
        : {}),
    })).filter((selection) => selection.option_id);
    const incomingCategories = new Set(incomingSelections.flatMap((selection) => {
      const option = optionsById.get(selection.option_id);
      const role = option?.product_role || "primary_choice";
      const category = productCategoryIdentity(option);
      return category && ["primary_choice", "optional_addon"].includes(role)
        ? [category]
        : [];
    }));
    setProductDraft((current) => {
      const retained = current.filter((selection) => {
        if (optionIds.has(String(selection.option_id || ""))) return false;
        const option = optionsById.get(String(selection.option_id || ""));
        const role = option?.product_role || "primary_choice";
        const category = productCategoryIdentity(option);
        return !(
          category
          && ["primary_choice", "optional_addon"].includes(role)
          && incomingCategories.has(category)
        );
      });
      const merged = new Map();
      [...retained, ...incomingSelections].forEach((selection) => {
        const key = String(selection.option_id || "");
        merged.delete(key);
        merged.set(key, selection);
      });
      return [...merged.values()];
    });
  };

  const updateProductQuantity = (optionId, quantity) => {
    const normalizedOptionId = String(optionId || "");
    const normalizedQuantity = Number(quantity);
    if (!normalizedOptionId || !Number.isInteger(normalizedQuantity) || normalizedQuantity < 1) {
      return;
    }
    setProductDraft((current) => current.map((selection) => (
      String(selection.option_id || "") === normalizedOptionId
        ? { ...selection, quantity: normalizedQuantity }
        : selection
    )));
  };

  const saveProductEdit = async () => {
    if (selectionEditBlocked) return;
    setProductEditError("");
    if (productEditSelectionError) {
      setProductEditError(productEditSelectionError);
      return;
    }
    setCheckoutNotice("");
    setProductEditBusy(true);
    try {
      const next = await requoteTreatmentCheckout(
        checkout,
        appliedCoupon?.code || "",
        productDraft,
      );
      activeCheckoutToken.current = persistRepricedCheckout(next, activeCheckoutToken.current);
      setCheckout(next);
      setEditingProducts(false);
      setProductDraft([]);
      setError("");
      setCheckoutRecovery(null);
    } catch (requoteError) {
      if (await recoverFromStaleCheckout(requoteError)) return;
      setProductEditError(
        requoteError?.code === "product_unavailable"
          || requoteError?.code === "product_unavailable_for_care_location"
          ? "That Product is no longer available for your care location. Choose another option."
          : getApiErrorMessage(requoteError)
            || requoteError?.message
            || "Your Product selection could not be updated. Please try again.",
      );
    } finally {
      setProductEditBusy(false);
    }
  };

  useEffect(() => {
    if (checkout) return undefined;
    let active = true;
    setCheckoutInitializing(true);
    api.getCombinedCheckoutRecovery(token)
      .then((recoveredCheckout) => {
        if (!active) return;
        const checkoutWithRuntimeApiBaseUrl = attachRuntimeApiBaseUrl(
          recoveredCheckout,
          clientConfig?.api_endpoint,
        );
        sessionStorage.setItem(
          treatmentCheckoutStorageKey(token),
          JSON.stringify(checkoutWithRuntimeApiBaseUrl),
        );
        setCheckout(checkoutWithRuntimeApiBaseUrl);
      })
      .catch((recoveryError) => {
        if (active) {
          setError(getApiErrorMessage(recoveryError) || "This checkout link is unavailable.");
        }
      })
      .finally(() => {
        if (active) setCheckoutInitializing(false);
      });
    return () => {
      active = false;
    };
  }, [checkout, token]);

  // The cached checkout is only a first paint. The server's checkout can have
  // changed since it was cached (staff can replace the product on a pending
  // order), and loading the recovery link retires the cached capability, so the
  // recovered checkout always replaces the cached one. Returns "updated" when
  // the quote changed, "refreshed" when only the capability did, or null when
  // nothing could be adopted and the cached copy is kept.
  const refreshCheckout = async (isActive = () => true) => {
    const tokenInUse = activeCheckoutToken.current;
    try {
      const recovered = await api.getCombinedCheckoutRecovery(tokenInUse);
      if (!isActive() || !isUsableCheckout(recovered)) return null;
      const { checkout: next, changed } = adoptRecoveredCheckout(
        checkoutRef.current,
        recovered,
        clientConfig?.api_endpoint,
      );
      sessionStorage.setItem(treatmentCheckoutStorageKey(tokenInUse), JSON.stringify(next));
      checkoutRef.current = next;
      setCheckout(next);
      if (changed) {
        setEditingProducts(false);
        setProductDraft([]);
        setProductEditError("");
        setCheckoutRecovery(null);
      }
      return changed ? "updated" : "refreshed";
    } catch (_error) {
      return null;
    }
  };

  // A payment or re-price rejected as stale is answered by reloading the
  // checkout, so the patient reviews the current order instead of restarting.
  const recoverFromStaleCheckout = async (requestError) => {
    if (!isStaleCheckoutError(requestError)) return false;
    const outcome = await refreshCheckout();
    if (!outcome) return false;
    setError("");
    setCheckoutNotice(
      outcome === "updated" ? CHECKOUT_UPDATED_NOTICE : CHECKOUT_REFRESHED_NOTICE,
    );
    return true;
  };

  useEffect(() => {
    if (!checkoutRef.current) return undefined;
    let active = true;
    refreshCheckout(() => active).then((outcome) => {
      if (active && outcome === "updated") setCheckoutNotice(CHECKOUT_UPDATED_NOTICE);
    });
    return () => {
      active = false;
    };
    // Revalidate once per checkout link; later changes come from the patient's own edits.
  }, [token]);

  useEffect(() => {
    if (activeCoupon?.error) {
      setCouponError(activeCoupon.error);
    }
  }, [activeCoupon?.error]);

  // Re-pricing issues a new capability server-side and invalidates the old
  // one, so the amount the patient authorizes always matches what they see.
  const applyCoupon = async (code) => {
    if (selectionEditBlocked) return;
    setCouponError("");
    setCheckoutNotice("");
    setCouponBusy(true);
    try {
      const next = await requoteTreatmentCheckout(checkout, code);
      activeCheckoutToken.current = persistRepricedCheckout(next, activeCheckoutToken.current);
      setCheckout(next);
      setCheckoutRecovery(null);
      setError("");
      setCouponInput("");
      const nextCoupon = next?.coupon || next?.pricing?.coupon;
      if (code && !nextCoupon?.code) {
        setCouponError(TREATMENT_CHECKOUT_ERROR_COPY.coupon_invalid);
      } else if (code && nextCoupon?.error) {
        setCouponError(nextCoupon.error);
      }
    } catch (requoteError) {
      if (await recoverFromStaleCheckout(requoteError)) return;
      setCouponError(
        TREATMENT_CHECKOUT_ERROR_COPY[requoteError?.code]
        || requoteError?.message
        || "That discount code could not be applied.",
      );
    } finally {
      setCouponBusy(false);
    }
  };
  const [brandLogoFailed, setBrandLogoFailed] = useState(false);
  const branding = {
    ...(clientConfig?.questionnaire_config || clientConfig?.branding_config || clientConfig || {}),
    // The checkout snapshot carries the tenant resolved for this particular
    // Program or Custom Program. It must win over the app's boot-time config,
    // which can be a generic/embed config after the route change.
    ...(checkout?.questionnaire_branding || {}),
  };
  const brandName = (
    branding.company_name
    || clientConfig?.name
    || "WellieMD"
  );
  let brandLogo = !brandLogoFailed ? pickQuestionnaireLogoUrl(branding) : "";
  if (brandLogo && brandLogo.includes("localstack")) {
    brandLogo = brandLogo.replace("localstack", "localhost");
  }

  useEffect(() => {
    document.body.classList.add("treatment-payment-route");
    return () => document.body.classList.remove("treatment-payment-route");
  }, []);

  useEffect(() => {
    if (!checkout?.checkout_capability) return undefined;
    let mounted = true;
    setPaymentMethodsLoading(true);
    setCheckoutPaymentConfig(null);
    setCheckoutSavedPaymentMethods([]);
    Promise.resolve(api.getCombinedCheckoutPaymentMethods({
      checkoutCapability: checkout.checkout_capability,
      runtimeSessionRevision: checkout.session_revision,
    })).then((response) => {
      if (!mounted) return;
      setCheckoutPaymentConfig(response?.payment_config || null);
      setCheckoutSavedPaymentMethods(
        Array.isArray(response?.saved_payment_methods)
          ? response.saved_payment_methods
          : [],
      );
    }).catch((paymentMethodsError) => {
      if (!mounted) return;
      // A saved-card lookup must never prevent a patient from entering a new
      // card. PaymentStep still loads the live gateway config as a fallback.
      setCheckoutPaymentConfig(null);
      setCheckoutSavedPaymentMethods([]);
      console.warn(
        "Could not load saved payment methods for treatment checkout:",
        paymentMethodsError?.message || paymentMethodsError,
      );
    }).finally(() => {
      if (mounted) setPaymentMethodsLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [checkout?.checkout_capability, checkout?.session_revision]);

  if (result) {
    return (
      <main className="treatment-payment-page">
        <div className="treatment-payment-page__brand">
          {brandLogo ? (
            <img
              src={brandLogo}
              alt={brandName}
              onError={() => setBrandLogoFailed(true)}
            />
          ) : (
            <strong>{brandName}</strong>
          )}
        </div>
        <TreatmentOrderSubmitted
          result={result}
          summary={summary}
          patientName={checkout?.patient_name}
        />
      </main>
    );
  }
  if (checkoutInitializing) {
    return (
      <main className="treatment-payment-page">
        <section className="treatment-payment-page__card">
          <h1>Loading checkout</h1>
          <p>We&apos;re securely restoring your checkout session.</p>
        </section>
      </main>
    );
  }

  if (checkout?.checkout_capability && !summary?.cart) {
    // The server owns every cart total. Without them there is nothing safe to
    // render, so ask for a fresh quote rather than showing a computed number.
    return (
      <main className="treatment-payment-page">
        <section className="treatment-payment-page__card">
          <h1>Checkout needs refreshing</h1>
          <p>Your pricing is out of date. Please restart the questionnaire to
            get an up-to-date total.</p>
        </section>
      </main>
    );
  }
  if (!checkout?.checkout_capability) {
    return (
      <main className="treatment-payment-page">
        <section className="treatment-payment-page__card">
          <h1>Checkout unavailable</h1>
          <p>This checkout link is unavailable. Please restart the questionnaire.</p>
        </section>
      </main>
    );
  }

  const treatments = summary?.treatments || [];
  // Every cart number is a server value rendered as-is. The page performs no
  // arithmetic on money: with two treatments a browser-side sum becomes a
  // second financial authority that can disagree with the amount authorized.
  const cart = summary?.cart || null;
  const itemCount = treatments.reduce(
    (count, treatment) => count
      + (treatment.products || []).reduce(
        (productCount, product) => productCount + 1 + (product.linked_supplies || []).length,
        0,
      )
      + (treatment.labs || []).length,
    0,
  );

  const submit = async () => {
    setError("");
    setCheckoutNotice("");
    setLoading(true);
    try {
      const paymentData = await paymentRef.current?.getPaymentData?.();
      const tokenValue = paymentMethodToken(paymentData);
      const paymentMethod = normalizePaymentMethod(paymentData);
      if (!tokenValue || !paymentMethod) throw new Error("Payment method is required.");
      const idempotencyKey = checkout.client_idempotency_key
        || globalThis.crypto?.randomUUID?.()
        || `checkout-${Date.now()}`;
      if (
        Array.isArray(checkoutRecovery?.recovery_actions)
        && checkoutRecovery.recovery_actions.includes("update_payment_method")
      ) {
        await api.recoverCombinedCheckout({
          checkoutCapability: checkout.checkout_capability,
          runtimeSessionRevision: checkout.session_revision,
          idempotencyKey,
          recoveryAction: "update_payment_method",
        });
        setCheckoutRecovery(null);
      }
      const response = await api.submitCombinedCheckout({
        checkoutCapability: checkout.checkout_capability,
        runtimeSessionRevision: checkout.session_revision,
        idempotencyKey,
        paymentMethodToken: tokenValue,
        paymentMethod,
      });
      clearTreatmentCheckout(activeCheckoutToken.current);
      if (activeCheckoutToken.current !== token) clearTreatmentCheckout(token);
      setResult({ ...response, success: isCombinedCheckoutSuccessful(response) });
    } catch (submitError) {
      if (await recoverFromStaleCheckout(submitError)) return;
      setCheckoutRecovery(submitError.checkoutRecovery || null);
      const productAvailabilityCodes = [
        "product_unavailable",
        "product_unavailable_for_care_location",
        "product_state_unavailable",
        "product_selection_stale",
        "no_fulfillable_products",
      ];
      if (productAvailabilityCodes.includes(submitError?.code)) {
        // The quote can become stale between render and submit. Keep the
        // patient on the same checkout and offer the release-bound options
        // immediately instead of forcing a restart or a second payment.
        beginProductEdit();
        setProductEditError(
          TREATMENT_CHECKOUT_ERROR_COPY[submitError.code]
            || "Review your Product selection and try again.",
        );
      }
      setError(
        TREATMENT_CHECKOUT_ERROR_COPY[submitError?.code]
          || getApiErrorMessage(submitError)
          || "Checkout could not be completed.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="treatment-payment-page">
      <div
        className="treatment-payment-page__progress"
        role="progressbar"
        aria-label="Treatment intake progress"
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow="100"
      >
        <span />
      </div>
      <div className="treatment-payment-page__brand">
        {brandLogo ? (
          <img
            src={brandLogo}
            alt={brandName}
            onError={() => setBrandLogoFailed(true)}
          />
        ) : (
          <strong>{brandName}</strong>
        )}
        <span className="treatment-payment-page__context">Checkout</span>
      </div>
      <header className="treatment-payment-page__header">
        <h1>Confirm and authorize</h1>
        <p>
          Review your treatment order. We&apos;ll authorize the total now—you&apos;re
          charged only when a doctor approves the prescription.
        </p>
      </header>
      {checkoutNotice && (
        <div className="treatment-payment-page__checkout-update" role="status">{checkoutNotice}</div>
      )}
      {error && <div className="treatment-payment-page__error">{error}</div>}
      {selectionEditBlocked && (
        <div className="treatment-payment-page__error" role="status">
          {selectionEdit.message || "Your checkout cannot be changed while payment is being confirmed."}
        </div>
      )}
      <div className={`treatment-payment-page__layout${editingProducts ? " is-editing-products" : ""}`}>
        <div className="treatment-payment-page__review">
          <section className="treatment-payment-page__card">
            {!editingProducts && (
              <span className="treatment-payment-page__section-label">
                Order summary · {itemCount} items
              </span>
            )}
            {!editingProducts && (
              <button
                type="button"
                className="treatment-payment-page__edit-products"
                onClick={beginProductEdit}
                disabled={loading || couponBusy || selectionEditBlocked}
              >
                Edit product selection
              </button>
            )}
            {editingProducts && (
              <section
                className="treatment-payment-page__product-editor"
                aria-label="Edit product selection"
              >
                <h2>Edit product selection</h2>
                <p>
                  Choose up to one product from each category and one supply
                  option per card. Products from different categories can be
                  selected together. Your total will be recalculated before
                  payment.
                </p>
                {(productEditError || productEditSelectionError) && (
                  <div className="treatment-payment-page__error" role="alert">
                    {productEditError || productEditSelectionError}
                  </div>
                )}
                {treatments.map((treatment) => {
                  const options = treatment.product_options || [];
                  const optionIds = new Set(options.map((option) => String(option.option_id || "")));
                  const selected = productDraft.filter((selection) => (
                    optionIds.has(String(selection.option_id || ""))
                  ));
                  if (!options.length) {
                    return (
                      <p key={treatment.treatment_type_id} role="alert">
                        Product options are unavailable for {treatment.name}.
                      </p>
                    );
                  }
                  return (
                    <React.Fragment key={treatment.treatment_type_id}>
                      <TreatmentProductSelection
                        question={{
                          selection_mode: "multiple",
                          min_selections: 1,
                          program_name: treatment.name,
                          treatment_type_name: treatment.name,
                          answer_choices: options,
                        }}
                        value={selected}
                        onChange={(next) => updateTreatmentProductDraft(treatment, next)}
                      />
                      {selected.map((selection) => {
                        const option = options.find((candidate) => (
                          String(candidate.option_id || "")
                            === String(selection.option_id || "")
                        ));
                        const quantities = (option?.allowed_quantities || [1])
                          .map(Number)
                          .filter((quantity) => Number.isInteger(quantity) && quantity > 0);
                        if (quantities.length < 2) return null;
                        return (
                          <label
                            className="treatment-payment-page__product-quantity"
                            key={`quantity:${selection.option_id}`}
                          >
                            Quantity for {option?.patient_label || option?.name || "Product"}
                            <select
                              value={Number(selection.quantity || quantities[0])}
                              onChange={(event) => updateProductQuantity(
                                selection.option_id,
                                event.target.value,
                              )}
                              aria-label={`Quantity for ${option?.patient_label || option?.name || "Product"}`}
                            >
                              {quantities.map((quantity) => (
                                <option value={quantity} key={quantity}>{quantity}</option>
                              ))}
                            </select>
                          </label>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
                <div className="treatment-payment-page__product-editor-actions">
                  <button
                    type="button"
                    onClick={cancelProductEdit}
                    disabled={productEditBusy}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="treatment-payment-page__edit-products-save"
                    onClick={saveProductEdit}
                    disabled={
                      productEditBusy
                      || selectionEditBlocked
                      || !productDraft.length
                      || Boolean(productEditSelectionError)
                    }
                  >
                    {productEditBusy ? "Updating total…" : "Update total"}
                  </button>
                </div>
              </section>
            )}
            {!editingProducts && treatments.map((treatment, treatmentIndex) => (
              <article
                className="treatment-payment-page__treatment"
                key={treatment.treatment_type_id || treatment.program_id || treatmentIndex}
              >
                <h2>{treatment.name || `Treatment ${treatmentIndex + 1}`}</h2>
                {(treatment.products || []).map((product, productIndex) => (
                  <React.Fragment key={product.option_id || product.product_id || productIndex}>
                    <div className="treatment-payment-page__product">
                      {resolveProductImageUrl(product) && (
                        <img
                          className="treatment-payment-page__product-image"
                          src={resolveProductImageUrl(product)}
                          alt=""
                          loading="lazy"
                          onError={(event) => { event.currentTarget.hidden = true; }}
                        />
                      )}
                      <div className="treatment-payment-page__product-copy">
                        <strong>
                          {patientProductName(product) || `Product ${productIndex + 1}`}
                        </strong>
                        <span>
                          {patientProductDescription(product)}
                        </span>
                      </div>
                      <strong className="treatment-payment-page__money">
                        {money(
                          product.medication_subtotal
                          || product.item_subtotal
                          || product.price
                          || product.line_total
                          || product.total,
                          summary?.currency,
                        )}
                      </strong>
                    </div>
                    {(product.linked_supplies || []).length > 0 && (
                      <div className="treatment-payment-page__supplies" aria-label="Supplies">
                        <span className="treatment-payment-page__supplies-label">Supplies</span>
                        {(product.linked_supplies || []).map((supply, supplyIndex) => (
                          <div
                            className="treatment-payment-page__supply"
                            key={supply.id || supply.name || supplyIndex}
                          >
                            <span>
                              {supply.name || "Supply"}
                              {Number(supply.quantity || 1) > 1
                                ? ` · Qty ${supply.quantity}`
                                : ""}
                            </span>
                            <strong className="treatment-payment-page__money">
                              {supply.is_included
                                ? "Included"
                                : money(
                                  supply.item_subtotal
                                  || supply.price
                                  || supply.line_total,
                                  summary?.currency,
                                )}
                            </strong>
                          </div>
                        ))}
                      </div>
                    )}
                  </React.Fragment>
                ))}
                {(treatment.labs || []).map((lab, labIndex) => (
                  <div
                    className="treatment-payment-page__lab"
                    key={lab.assignment_id || lab.requirement_id || lab.panel_id || labIndex}
                  >
                    <div className="treatment-payment-page__product-copy">
                      <strong>{lab.name || "Laboratory panel"}</strong>
                      <span>
                        <span className="treatment-payment-page__lab-reference">Lab</span>
                        {` ${patientLabDescription(lab)}`}
                      </span>
                    </div>
                    <strong className="treatment-payment-page__money">
                      {money(patientLabPrice(lab), summary?.currency)}
                    </strong>
                  </div>
                ))}
                {treatments.length > 1 && (
                  <div className="treatment-payment-page__treatment-total">
                    <span>{treatment.name || "Treatment"} total</span>
                    <strong className="treatment-payment-page__money">
                      {money(treatment.total, summary?.currency)}
                    </strong>
                  </div>
                )}
              </article>
            ))}
            {!editingProducts && <div className="treatment-payment-page__totals">
              <div className="treatment-payment-page__total-row">
                <span>Products</span>
                <span className="treatment-payment-page__money">
                  {money(cart?.product_subtotal, summary?.currency)}
                </span>
              </div>
              <div className="treatment-payment-page__total-row">
                <span>Shipping</span>
                <span className="treatment-payment-page__money">
                  {Number(cart?.product_shipping || 0) > 0
                    ? money(cart?.product_shipping, summary?.currency)
                    : "Free"}
                </span>
              </div>
              {Number(cart?.product_discount || 0) > 0 && (
                <div className="treatment-payment-page__total-row is-discount">
                  <span>
                    Discount
                    {appliedCoupon?.code ? ` · ${appliedCoupon.code}` : ""}
                  </span>
                  <span className="treatment-payment-page__money">
                    −{money(cart?.product_discount, summary?.currency)}
                  </span>
                </div>
              )}
              {Number(cart?.lab_total || 0) > 0 && (
                <div className="treatment-payment-page__total-row">
                  <span>Labs</span>
                  <span className="treatment-payment-page__money">
                    {money(cart.lab_total, summary?.currency)}
                  </span>
                </div>
              )}
              <div className="treatment-payment-page__coupon">
                {appliedCoupon ? (
                  <div className="treatment-payment-page__coupon-applied">
                    <div className="treatment-payment-page__coupon-applied-info">
                      <svg className="treatment-payment-page__coupon-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                        <line x1="7" y1="7" x2="7.01" y2="7"/>
                      </svg>
                      <span>{`Discount code ${appliedCoupon.code} applied`}</span>
                    </div>
                    <button
                      type="button"
                      className="treatment-payment-page__coupon-remove-btn"
                      disabled={couponBusy || loading || editingProducts || selectionEditBlocked}
                      onClick={() => applyCoupon("")}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="treatment-payment-page__coupon-entry">
                    <label htmlFor="treatment-checkout-coupon">
                      <svg
                        className="treatment-payment-page__coupon-label-icon"
                        aria-hidden="true"
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                        <line x1="7" y1="7" x2="7.01" y2="7"/>
                      </svg>
                      <span>Promo or discount code</span>
                    </label>
                    <div className="treatment-payment-page__coupon-input-group">
                      <input
                        id="treatment-checkout-coupon"
                        value={couponInput}
                        placeholder="ENTER CODE"
                        autoComplete="off"
                        disabled={couponBusy || loading || editingProducts || selectionEditBlocked}
                        onChange={(event) => setCouponInput(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" && couponInput.trim() && !couponBusy && !loading) {
                            event.preventDefault();
                            applyCoupon(couponInput.trim());
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="treatment-payment-page__coupon-apply-btn"
                        disabled={couponBusy || loading || editingProducts || selectionEditBlocked || !couponInput.trim()}
                        onClick={() => applyCoupon(couponInput.trim())}
                      >
                        {couponBusy ? "Applying…" : "Apply"}
                      </button>
                    </div>
                  </div>
                )}
                {couponError && (
                  <p className="treatment-payment-page__coupon-error" role="alert">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <span>{couponError}</span>
                  </p>
                )}
              </div>
              <div className="treatment-payment-page__total-row is-total">
                <span>
                  {treatments.length > 1
                    ? `${treatments.length} treatments — authorized today`
                    : "Treatments — authorized today"}
                </span>
                <span className="treatment-payment-page__money">
                  {money(cart?.grand_total, summary?.currency)}
                </span>
              </div>
            </div>}
          </section>

          {!editingProducts && <section className="treatment-payment-page__notice" aria-label="How payment works">
            <span className="treatment-payment-page__notice-icon" aria-hidden="true">i</span>
            <div>
              <strong>How payment works</strong>
              <ul>
                <li>Each treatment is authorized today and charged only after doctor approval.</li>
                <li>A lower-priced prescription can produce a lower final charge.</li>
                <li>A higher-priced prescription follows the approved payment recovery flow before capture.</li>
                <li>If treatment is declined, the authorization hold is released without a charge.</li>
              </ul>
            </div>
          </section>}
        </div>

        {!editingProducts && <section className="treatment-payment-page__card treatment-payment-page__payment">
          <div className="treatment-payment-page__payment-header">
            <h2>Payment method</h2>
            <span className="treatment-payment-page__secure">Secure</span>
          </div>
          {paymentMethodsLoading ? (
            <div className="treatment-payment-page__payment-loading">
              Loading payment options...
            </div>
          ) : (
            <PaymentStep
              ref={paymentRef}
              mode="checkout"
              checkoutOrderSummary={summary}
              checkoutPaymentConfig={checkoutPaymentConfig}
              checkoutSavedPaymentMethods={checkoutSavedPaymentMethods}
              checkoutCapability={checkout.checkout_capability}
              checkoutSessionRevision={checkout.session_revision}
              hideCheckoutOrderSummary
              hidePaymentHeading
              clientConfig={clientConfig}
              onError={(paymentError) => (
                setError(paymentError?.message || String(paymentError))
              )}
            />
          )}
          <button
            type="button"
            className="treatment-payment-page__submit"
            onClick={submit}
            disabled={loading || paymentMethodsLoading || editingProducts || productEditBusy}
          >
            {loading
              ? "Processing..."
              : checkoutRecovery?.recovery_actions?.includes("update_payment_method")
                ? "Retry authorization"
                : "Authorize and submit"}
          </button>
          <p className="treatment-payment-page__consent">
            By authorizing, you agree to per-treatment capture as each case is reviewed.
          </p>
        </section>}
      </div>
    </main>
  );
}
